/* eslint-disable no-unused-vars */
import { Toaster } from 'react-hot-toast';
// Pre-commit hook test - all tests should pass now
import { Route, BrowserRouter as Router, Routes, Navigate } from 'react-router-dom';
import React, { lazy, Suspense } from 'react';
import { ReduxProvider } from './store/ReduxProvider';
import { ThemeProvider } from './contexts/ThemeContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { WorkspaceExperienceProvider } from './context/WorkspaceExperienceContext';
import { ConfirmDialogProvider } from './contexts/ConfirmDialogContext';
import { useAppSelector } from './store/hooks';
import { selectUser } from './store/slices/authSlice';
import { Loader } from './components/Loader/Loader';
// Eager: avoids Fast Refresh suspending the lazy Agents chunk when AgentForm/etc. HMR.
import Agents from './components/Agents/Agents';

// Lazy load route components
const Analytics = lazy(() => import('./components/Analytics/Analytics'));
const Calendar = lazy(() => import('./components/Calendar/Calendar'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const Integrations = lazy(() => import('./components/Integrations/Integrations'));
const Layout = lazy(() => import('./components/Layout/Layout'));
const MCPServers = lazy(() => import('./components/MCPServers/MCPServers'));
const Settings = lazy(() => import('./components/Settings/Settings'));
const Testing = lazy(() => import('./components/Testing/Testing'));
const VoipCalling = lazy(() => import('./components/VoipCalling/VoipCalling'));
const Transcripts = lazy(() => import('./components/Transcripts/Transcripts'));
const PaymentPage = lazy(() => import('./components/Payments/PaymentPage'));
const CallMe = lazy(() => import('./components/CallMe/CallMe'));
const PhoneNumbers = lazy(() => import('./components/Telephony/PhoneNumbers'));
const LoginForm = lazy(() => import('./components/Auth/LoginForm'));
const RegisterForm = lazy(() => import('./components/Auth/RegisterForm'));
const ForgotPasswordForm = lazy(() => import('./components/Auth/ForgotPasswordForm'));
const ResetPasswordForm = lazy(() => import('./components/Auth/ResetPasswordForm'));
const AuthCallback = lazy(() => import('./components/Auth/AuthCallback'));
const ProtectedRoute = lazy(() => import('./components/Auth/ProtectedRoute'));
const LandingPage = lazy(() => import('./components/Landing/LandingPage'));
const AuthLayout = lazy(() => import('./components/Auth/AuthLayout'));
const PermissionGuard = lazy(() => import('./components/Guards/PermissionGuard'));
const AdminGuard = lazy(() => import('./components/Guards/AdminGuard'));

// Admin components
const AdminLayout = lazy(() => import('./admin/components/AdminLayout'));
const AdminDashboard = lazy(() => import('./admin/pages/Dashboard'));
const PlansPage = lazy(() => import('./admin/pages/Plans'));
const UsersPage = lazy(() => import('./admin/pages/Users'));
const ProvidersPage = lazy(() => import('./admin/pages/Providers'));
const DefaultsPage = lazy(() => import('./admin/pages/Defaults'));
const IntegrationsPage = lazy(() => import('./admin/pages/Integrations'));
const AgentsPage = lazy(() => import('./admin/pages/Agents'));
const UserDetailsPage = lazy(() => import('./admin/pages/UserDetails'));
const LanguagesPage = lazy(() => import('./admin/pages/Languages'));
const UseCasesPage = lazy(() => import('./admin/pages/UseCases'));
const ArticlesPage = lazy(() => import('./admin/pages/Articles'));
const PaymentsPage = lazy(() => import('./admin/pages/Payments'));
const UsagePage = lazy(() => import('./admin/pages/Usage'));
const FeatureTogglesPage = lazy(() => import('./admin/pages/FeatureToggles'));
const CostDashboardPage = lazy(() => import('./admin/pages/CostDashboard'));
const CallMeSettingsPage = lazy(() => import('./admin/pages/CallMeSettings'));
const RootProviderConfigsPage = lazy(() => import('./admin/pages/RootProviderConfigs'));
const CallCampaignsAdminPage = lazy(() => import('./admin/pages/CallCampaigns'));
const AdminTestingPage = lazy(() => import('./admin/pages/Testing'));
const AdminLogsPage = lazy(() => import('./admin/pages/Logs'));

// Articles and Legal
const UserManagement = lazy(() => import('./components/UserManagement/UserManagement'));
const HealthCheck = lazy(() => import('./components/HealthCheck/HealthCheck'));
const ArticlesList = lazy(() => import('./components/Articles/ArticlesList'));
const TwilioIntegrationArticle = lazy(() => import('./components/Articles/TwilioIntegrationArticle'));
const ZapierTranscriptionsWebhookArticle = lazy(() => import('./components/Articles/ZapierTranscriptionsWebhookArticle'));
const ZapierRecordingsWebhookArticle = lazy(() => import('./components/Articles/ZapierRecordingsWebhookArticle'));
const CallRecordingsAndTranscriptsArticle = lazy(() => import('./components/Articles/CallRecordingsAndTranscriptsArticle'));
const AwsPollyIntegrationArticle = lazy(() => import('./components/Articles/AwsPollyIntegrationArticle'));
const ArticleMarkdownPage = lazy(() => import('./components/Articles/ArticleMarkdownPage'));
const PrivacyPolicy = lazy(() => import('./components/Legal/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./components/Legal/TermsAndConditions'));
const ContactPage = lazy(() => import('./components/Contact/ContactPage'));
const CookieConsentBanner = lazy(() => import('./components/Cookies/CookieConsentBanner'));
const WhatsAppWidget = lazy(() => import('./components/WhatsApp/WhatsAppWidget'));
const GoogleAnalytics = lazy(() => import('./components/Analytics/GoogleAnalytics'));
const CostCalculatorAdmin = lazy(() => import('./components/CostCalculator/CostCalculatorAdmin'));
const KnowledgePage = lazy(() => import('./pages/KnowledgePage'));
const DevelopPage = lazy(() => import('./pages/DevelopPage'));
const WorkflowsPage = lazy(() => import('./pages/WorkflowsPage'));
const SalesRunsPage = lazy(() => import('./pages/SalesRunsPage'));
const SalesRunDetailPage = lazy(() => import('./pages/SalesRunDetailPage'));
const EmbedPage = lazy(() => import('./pages/EmbedPage'));

function AdminRedirect({ children }) {
  const user = useAppSelector(selectUser);
  if (user && (user.role === 'admin' || user.role === 'super-admin')) {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function App() {
  return (
    <ReduxProvider>
      <ThemeProvider>
        <LoadingProvider>
          <ConfirmDialogProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <WorkspaceExperienceProvider>
            <GoogleAnalytics />
            <CookieConsentBanner />
            <WhatsAppWidget />
            <div className="ai-bg">
            <Suspense fallback={<Loader loading={true} size="fullscreen" fullscreen />}>
              <Routes>
                {/* Public routes */}
                <Route
                  path="/login"
                  element={
                    <AuthLayout>
                      <LoginForm />
                    </AuthLayout>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <AuthLayout>
                      <RegisterForm />
                    </AuthLayout>
                  }
                />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route
                  path="/forgot-password"
                  element={
                    <AuthLayout>
                      <ForgotPasswordForm />
                    </AuthLayout>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <AuthLayout>
                      <ResetPasswordForm />
                    </AuthLayout>
                  }
                />

                {/* Public landing page */}
                <Route
                  path="/"
                  element={
                    <AdminRedirect>
                      <LandingPage />
                    </AdminRedirect>
                  }
                />

                {/* Public health check page */}
                <Route path="/system-health" element={<HealthCheck />} />
                <Route path="/health" element={<HealthCheck />} />

                {/* Public legal pages */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/embed/:agentId" element={<EmbedPage />} />

                {/* Public articles routes */}
                <Route path="/articles" element={<ArticlesList />} />
                <Route path="/articles/twilio-integration" element={<TwilioIntegrationArticle />} />
                <Route
                  path="/articles/zapier/transcriptions-webhook"
                  element={<ZapierTranscriptionsWebhookArticle />}
                />
                <Route
                  path="/articles/zapier/recordings-webhook"
                  element={<ZapierRecordingsWebhookArticle />}
                />
                <Route
                  path="/articles/call-recordings-and-transcripts"
                  element={<CallRecordingsAndTranscriptsArticle />}
                />
                <Route
                  path="/articles/aws-polly-integration"
                  element={<AwsPollyIntegrationArticle />}
                />
                <Route path="/articles/docs/:docSlug" element={<ArticleMarkdownPage />} />

                {/* Protected routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <AdminRedirect>
                          <Layout>
                            <Dashboard />
                          </Layout>
                        </AdminRedirect>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                {/* Redirect /home to /dashboard */}
                <Route path="/home" element={<Navigate to="/dashboard" replace />} />
                <Route path="/developer" element={<Navigate to="/develop" replace />} />
                <Route
                  path="/agents"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <Agents />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route path="/cost-calculator" element={<CostCalculatorAdmin />} />
                <Route
                  path="/integrations"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <Integrations />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <Calendar />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <Analytics />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                {/* No /plans route per requirement */}
                <Route
                  path="/payment"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <PaymentPage />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/testing"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <Testing />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/voip-calling"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <VoipCalling />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/telephony"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <PhoneNumbers />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/call-logs"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <Transcripts />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/call-me"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <CallMe />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/mcp"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <MCPServers />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/knowledge"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <KnowledgePage />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/develop"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <DevelopPage />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workflows"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <WorkflowsPage />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sales"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <SalesRunsPage />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sales/runs/:id"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <SalesRunDetailPage />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <Settings />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user-management"
                  element={
                    <ProtectedRoute>
                      <PermissionGuard>
                        <Layout>
                          <UserManagement />
                        </Layout>
                      </PermissionGuard>
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes - Completely isolated from user routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminGuard>
                        <AdminLayout />
                      </AdminGuard>
                    </ProtectedRoute>
                  }
                >
                  {/* Admin pages - New modular structure */}
                  <Route index element={<AdminDashboard />} />
                  <Route path="plans" element={<PlansPage />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="users/:id" element={<UserDetailsPage />} />
                  <Route path="agents" element={<AgentsPage />} />
                  <Route path="testing" element={<AdminTestingPage />} />
                  <Route path="integrations" element={<IntegrationsPage />} />
                  <Route path="providers" element={<ProvidersPage />} />
                  <Route path="defaults" element={<DefaultsPage />} />
                  <Route path="languages" element={<LanguagesPage />} />
                  <Route path="use-cases" element={<UseCasesPage />} />
                  <Route path="articles" element={<ArticlesPage />} />
                  <Route path="payments" element={<PaymentsPage />} />
                  <Route path="usage" element={<UsagePage />} />
                  {/* <Route path="monitor" element={<MonitorPage />} /> Reserved for future */}
                  <Route path="toggles" element={<FeatureTogglesPage />} />
                  <Route path="costs" element={<CostDashboardPage />} />
                  <Route path="callme-settings" element={<CallMeSettingsPage />} />
                  <Route path="root-provider-configs" element={<RootProviderConfigsPage />} />
                  <Route path="call-campaigns" element={<CallCampaignsAdminPage />} />
                  <Route path="logs" element={<AdminLogsPage />} />
                </Route>
              </Routes>
            </Suspense>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    theme: {
                      primary: '#4aed88',
                    },
                  },
                }}
              />
            </div>
            </WorkspaceExperienceProvider>
          </Router>
          </ConfirmDialogProvider>
        </LoadingProvider>
      </ThemeProvider>
    </ReduxProvider>
  );
}

export default App;
